import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { take} from 'rxjs/operators';
import { Constants } from 'src/app/order/constants/constants';
import { PersonTypeEnum } from 'src/app/order/enums/person-type.enum';
import { cpfCnpjMask } from 'src/app/order/masks/document-masks';
import { Messages } from 'src/app/order/messages/order.messages';
import { CnaeService } from 'src/app/order/services/external/cnae/cnae.service';
import { FeeService } from 'src/app/order/services/external/fee/fee.service'
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { sanitizeCpfCnpj } from 'src/app/order/utils/sanitize-search-filter';
import { CpfCnpjAlphanumericValidator } from 'src/app/order/validators/cpf-cnpj-validator';
import { InputType } from 'src/app/shared/fd-form-components/fd-input/fd-input.component';
import { FdSelectConfig } from 'src/app/shared/fd-form-components/fd-select/fd-select.component';
import { ClientInformationModel } from 'src/app/shared/models/simulation.model';
import { FdFieldConfigs } from 'src/app/shared/shared-components.module';
import { CnpjAndCnaeMccResponseDTO } from '../../simulation-economical-group/models/cnpj-and-cnae-mcc-response-dto.model';

interface CnaeMccInfo {
  mcc?: string | number;
  mccAbecs?: string | number;
  abecsMcc?: string | number;
}

@Component({
    selector: 'app-client-information',
    templateUrl: './client-information.component.html',
    styleUrls: ['./client-information.component.scss'],
    standalone: false
})
export class ClientInformationComponent implements OnInit {

  private readonly CEF_SERVICE_CONTRACT = '149';
  private readonly BILLING_VALUE_30M="30000000";
  private readonly NOT_APPLICABLE_ABECS = 'Não se aplica';

  @Input() clientInformation = new ClientInformationModel();
  @Output() consultOffers = new EventEmitter<ClientInformationModel>();
  @Output() cleanDown = new EventEmitter<ClientInformationModel>();

  formGroup: UntypedFormGroup = this.createFormGroup();
  fields: FdFieldConfigs = this.createFields();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private cnaeService: CnaeService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected scrollService: ScrollService,
    protected matDialog: MatDialog,
    protected routingService: RoutingService,
    private route: ActivatedRoute,
    private dataStoreService: DataStoreService,
    private feeService: FeeService
  ) {
  }

  ngOnInit() {
    this.checkParams();
  }

  private readonly CPF_REGEX = /^\d{11}$/;
  private readonly CNPJ_REGEX = /^([A-Za-z\d]){12}(\d){2}$/;

  get formControls(): { [key: string]: AbstractControl } {
    return this.formGroup.controls;
  }

  get isCPF(): boolean {
    const document = sanitizeCpfCnpj(this.formControls.cpfCnpj.value);
    return !!document && document.length === 11 && this.CPF_REGEX.test(document);
  }

  private checkParams() {
      this.formControls.billingValue.setValue(this.clientInformation.billingValue);
      this.formControls.cpfCnpj.setValue(sessionStorage.getItem(Constants.CPF_CNPJ));
      this.formControls.state.setValue(sessionStorage.getItem(Constants.UF));
      this.formControls.mccCnae.setValue(this.clientInformation.mccCnae || '');
      this.formControls.mccAbecs.setValue(this.clientInformation.mccAbecs || this.NOT_APPLICABLE_ABECS);
      const cnaeParam = sessionStorage.getItem(Constants.CNAE);
      if (cnaeParam) {
        this.getCnaeList(cnaeParam, true);
      }
  }

  private loadMccAbecsByDocument() {
    const document = sanitizeCpfCnpj(this.formControls.cpfCnpj.value);

    if (!document || document.length !== 14 || !this.CNPJ_REGEX.test(document)) {
      this.formControls.mccAbecs.setValue(this.NOT_APPLICABLE_ABECS);
      return;
    }

    this.cnaeService.getMccAbecsByCnpj(document).pipe(take(1)).subscribe((mccAbecs) => {
      this.formControls.mccAbecs.setValue(mccAbecs || this.NOT_APPLICABLE_ABECS);
    }, () => {
      this.formControls.mccAbecs.setValue(this.NOT_APPLICABLE_ABECS);
    });
  }

  get isCNPJ(): boolean {
    const document = sanitizeCpfCnpj(this.formControls.cpfCnpj.value);
    return !!document && document.length === 14 && this.CNPJ_REGEX.test(document);
  }

  getCnaeList(filterValue: string, loadingPage: boolean) {

    // No caso de CNPJ, os CNAE's virão populados da tela anterior (consulta do Serpro).
    // update 06/2021 do item acima. Não mais. A Serpro será consultada na última tela, então o cnae será selecionado manualmente e validado posteriormente.
    if (!filterValue || !filterValue.length) {
      this.clearMccFields();
      return;
    }

    if (filterValue.length < 3) {
      this.cnaeDropdown.items = [];
      return;
    }

    this.cnaeService.getCnaeList(filterValue)
      .pipe(take(1))
      .subscribe(cnaeList => {
        if (cnaeList != null) {
          this.cnaeDropdown.items = [];
          this.cnaeDropdown.items.push(...cnaeList);
          this.isCnaeLoaded.next(true);
          const currentItem = this.cnaeDropdown.items.find((x) => x.value == filterValue);
          if (loadingPage && currentItem) {
            currentItem.selected = true;
            this.formControls.cnaeNumber.setValue(currentItem.value);
            this.loadMccInfo(currentItem.value as string);
          }
        }
      },
      () => {
        this.cnaeDropdown.items = [];
      });
  }

  private loadMccInfo(cnae: string) {
    const cnpj = sanitizeCpfCnpj(this.formControls.cpfCnpj.value);
    if (!cnae) {
      this.clearMccFields();
      return;
    }

    this.cnaeService.getCnpjAndCnaeMccInfo(cnpj, cnae).pipe(take(1)).subscribe((res) => {
      const raw = res?.response;
      const items: CnpjAndCnaeMccResponseDTO[] = Array.isArray(raw)
          ? raw
          : raw != null ? [raw] : [];
      
      const response = items[0] as CnpjAndCnaeMccResponseDTO;
      
      const mccByCnae = response.mccCnae ? response.mccCnae + ' - ' + (response?.mccCnaeDescription?.split('-')[1] || '') : '';
      const mccByAbecs = response.mccAbecs ? response.mccAbecs + ' - ' + (response?.mccAbecsDescription || '') : this.NOT_APPLICABLE_ABECS;

        this.formControls.mccCnae.setValue(mccByCnae);
        if (!this.hasAbecsMccValue()) {
          this.formControls.mccAbecs.setValue(mccByAbecs || this.NOT_APPLICABLE_ABECS);
        }
        return;
    }, () => {
      this.clearMccFields();
    });
  }

  private clearMccFields() {
    this.formControls.mccCnae.setValue('');
    if (!this.hasAbecsMccValue()) {
      this.formControls.mccAbecs.setValue(this.NOT_APPLICABLE_ABECS);
    }
  }

  hasAbecsMccValue() {
    const currentAbecsValue = this.formControls.mccAbecs.value;
    return !!currentAbecsValue && currentAbecsValue !== this.NOT_APPLICABLE_ABECS;
  }

  private getPreferredMccNumber() {
    const rawAbecs = this.formControls.mccAbecs.value;
    const rawMccAbecsCodeWithoutDescription = rawAbecs?.split('-')[0]?.trim();
    const mccAbecs = rawAbecs === this.NOT_APPLICABLE_ABECS ? 0 : Number(rawMccAbecsCodeWithoutDescription);

    const rawMccCnae = this.formControls.mccCnae.value;
    const rawMccCnaeCodeWithoutDescription = rawMccCnae?.split('-')[0]?.trim();
    const mccCnae = Number(rawMccCnaeCodeWithoutDescription);

    if (mccAbecs) {
      return mccAbecs;
    }

    if (mccCnae) {
      return mccCnae;
    }

    return null;
  }

  isCnaeLoaded: Subject<boolean> = new Subject();

  get cnaeDropdown() {
    return (this.fields.cnaeNumber as FdSelectConfig);
  }

  protected createFields() {
    return {
      cpfCnpj: {
        label: 'CPF/CNPJ',
        controlName: 'cpfCnpj',
        type: InputType.TEL,
        mask: cpfCnpjMask,
        toUpperCase: true,
        maskCharsReplace: /[./ -]/g,
        messages: {
          required: 'informe o CPF/CNPJ',
          invalidCnpj: 'CNPJ inválido',
          invalidCpf: 'CPF inválido'
        }
      },
      state: {
        label: 'Estado (UF)',
        controlName: 'state',
        messages: {
          required: 'informe o estado',
          invalid: 'Selecione um estado'
        }
      },
      cnaeNumber: {
        label: 'CNAE',
        items: [],
        searchable: true,
        searchPlaceholder: 'Insira o CNAE',
        controlName: 'cnaeNumber',
        messages: {
          required: 'informe o CNAE',
          invalid: 'CNAE inválido'
        }
      },
      mccCnae: {
        label: 'MCC relacionado ao CNAE',
        controlName: 'mccCnae',
        type: InputType.TEL,
        isNumber: true,
        messages: {
          invalid: 'MCC inválido'
        }
      },
      mccAbecs: {
        label: 'MCC indicado pela ABECs',
        controlName: 'mccAbecs',
        type: InputType.TEL,
        isNumber: true,
        messages: {
          invalid: 'MCC inválido'
        }
      },
      billingValue: {
        label: 'Informe faturamento anual no cartão',
        controlName: 'billingValue',
        messages: {
          required: 'informe o faturamento anual',
          invalid: 'faturamento inválido',
          min: 'faturamento inválido',
          max: 'faturamento inválido'
        }
      },
    };
  }

  protected createFormGroup() {
    return this.formBuilder.group({
      cpfCnpj: [{value: '', disabled: true}, [Validators.required, CpfCnpjAlphanumericValidator]],
      cnaeNumber: [{value: '', disabled: true}, Validators.required],
      mccCnae: [{value: '', disabled: true}, () => null],
      mccAbecs: [{value: '', disabled: true}, () => null],
      billingValue: ['', [Validators.required, Validators.min(0.01), Validators.max(999999999)]],
      state: [{value: '', disabled: true}, Validators.required]
    });
  }

  checkCnae() {
    return this.cnaeService.checkCnae(this.formGroup.controls['cnaeNumber'].value, this.CEF_SERVICE_CONTRACT).toPromise()
      .then(data => {
        if (!data) {
          this.dialogService.openDialogWithHtmlMessage(Messages.UNWANTED_CNAE);
        }
        return data;
      });
  }

  isBillingValueEqualsOrAboveThirtyMillions(){
    const billingValue = this.formGroup.controls['billingValue'].value;
    return (billingValue >= this.BILLING_VALUE_30M)
  }

  public async showFeeData() {
    if(this.isBillingValueEqualsOrAboveThirtyMillions()){
      this.dialogService.openDialogWithHtmlMessage(Messages.BILLING_VALUE_EQUALS_OR_ABOVE_THIRTY_MILLIONS);
      return;
    }

    const checkCnae = await this.checkCnae();

    if (!checkCnae) {
      return;
    }

    const personType = this.getPersonType()

    this.consultOffers.emit({
      ...this.formGroup.getRawValue(),
      personType,
      mccNumber: this.getPreferredMccNumber()
    });

    this.cleanWhenFormValueChanges();
  }

  getPersonType() {
    if(this.isCNPJ) {
      return PersonTypeEnum.LEGAL
    }
    return this.isCPF ? PersonTypeEnum.PHYSICAL : '';
  }

  private cleanWhenFormValueChanges() {
    this.formGroup.valueChanges
      .pipe(take(1))
      .subscribe(() => {
        this.cleanDown.emit(undefined);
      })
  }

}
